/**
 * AgeBreakdownSection.tsx — Looker Studio style
 *
 * Layout:
 *   Bar chart (Opt-ins & Clicks by Age) with KPI stat cards
 *   aligned directly under each age group's bar column.
 */

import React from "react";
import {
  ComposedChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { branding } from "../../../../config/branding";
import type { AgeBreakdownRow } from "../ageBreakdown";
import { formatCurrency, formatNumber } from "../formatters";

// ─────────────────────────────────────────────
// DESIGN TOKENS (consistent with other breakdown sections)
// ─────────────────────────────────────────────
const C = branding.colors;
const COLOR_OPTINS = C.chart[0];                     // green-500 — opt-ins bar
const COLOR_SPEND  = C.chart[3];                     // green-300 — spend bar
const COLOR_CPL    = C.device?.ios ?? "#3b82f6";     // blue      — CPL line

const TICK_STYLE = { fontSize: 10, fill: "currentColor", opacity: 0.5 } as const;
const AXIS_PROPS = { axisLine: false, tickLine: false, tick: TICK_STYLE } as const;
const GRID_PROPS = { strokeDasharray: "3 3", vertical: false, stroke: "currentColor", strokeOpacity: 0.08 } as const;
const CHART_H = 220;
const Y_AXIS_W = 44;
const CHART_MARGIN = { top: 4, right: 8, bottom: 0, left: 0 } as const;

// ─────────────────────────────────────────────
// SHARED CARD WRAPPER
// ─────────────────────────────────────────────
function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="text-sm font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────
function AgeTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; fill?: string; stroke?: string; color?: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const fmt: Record<string, (v: number) => string> = {
    optins: v => formatNumber(v),
    spend:  v => formatCurrency(v),
    cpl:    v => formatCurrency(v),
  };
  const names: Record<string, string> = { optins: "Opt-ins", spend: "Spend", cpl: "Cost / Opt-in" };
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-xl text-xs">
      <p className="text-[10px] font-semibold text-muted-foreground mb-1.5">Age {label}</p>
      {payload.map(p => {
        const color = p.fill ?? p.stroke ?? p.color ?? "";
        return (
          <div key={p.dataKey} className="flex items-center gap-2 mb-0.5 last:mb-0">
            <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: color }} />
            <span className="text-muted-foreground">{names[p.dataKey] ?? p.dataKey}:</span>
            <span className="font-semibold text-foreground">{fmt[p.dataKey]?.(p.value) ?? String(p.value)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// SECTION COMPONENT
// ─────────────────────────────────────────────
interface AgeBreakdownSectionProps {
  data: AgeBreakdownRow[];
}

export function AgeBreakdownSection({ data }: AgeBreakdownSectionProps) {
  const numFmt   = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));
  const curFmt   = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
  const cplFmt   = (v: number) => `$${v.toFixed(0)}`;

  const chartData = data.map(row => ({
    ...row,
    cpl: row.optins > 0 ? row.spend / row.optins : 0,
  }));

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-3 pt-2">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-2">
          Age Breakdown
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <ChartCard title="Opt-ins &amp; Spend by Age">
        {/* Legend */}
        <div className="flex items-center gap-5 mb-3">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: COLOR_OPTINS }} />
            <span className="text-[10px] text-muted-foreground">Opt-ins</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: COLOR_SPEND }} />
            <span className="text-[10px] text-muted-foreground">Spend</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-[2px] rounded-full" style={{ background: COLOR_CPL }} />
            <span className="text-[10px] text-muted-foreground">Cost / Opt-in</span>
          </div>
        </div>

        {/* Composed chart: 2 bars + 1 line */}
        <div style={{ height: CHART_H }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={CHART_MARGIN}
              barCategoryGap="35%"
              barGap={3}
            >
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="age" {...AXIS_PROPS} dy={6} />

              {/* Left axis — Opt-ins count */}
              <YAxis yAxisId="optins" {...AXIS_PROPS} tickFormatter={numFmt} width={Y_AXIS_W} />

              {/* Right axis — Spend $ */}
              <YAxis yAxisId="spend" orientation="right" {...AXIS_PROPS} tickFormatter={curFmt} width={52} />

              {/* Hidden axis — CPL (own scale so the line isn't squished against spend axis) */}
              <YAxis yAxisId="cpl" hide />

              <Tooltip
                content={props => (
                  <AgeTooltip
                    active={props.active}
                    payload={props.payload?.map(p => ({
                      dataKey: String(p.dataKey),
                      value: Number(p.value),
                      fill:   String(p.fill   ?? ""),
                      stroke: String(p.stroke ?? ""),
                      color:  String(p.color  ?? ""),
                    }))}
                    label={String(props.label ?? "")}
                  />
                )}
                cursor={{ fill: "currentColor", fillOpacity: 0.04 }}
              />

              <Bar yAxisId="optins" dataKey="optins" fill={COLOR_OPTINS} fillOpacity={0.9} radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false} />
              <Bar yAxisId="spend"  dataKey="spend"  fill={COLOR_SPEND}  fillOpacity={0.9} radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false} />
              <Line
                yAxisId="cpl"
                dataKey="cpl"
                stroke={COLOR_CPL}
                strokeWidth={2}
                dot={{ r: 4, fill: COLOR_CPL, strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 0 }}
                connectNulls
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* KPI cards aligned under each bar group */}
        <div
          className="grid mt-3"
          style={{
            gridTemplateColumns: `repeat(${data.length}, 1fr)`,
            paddingLeft: Y_AXIS_W + CHART_MARGIN.left,
            paddingRight: CHART_MARGIN.right,
          }}
        >
          {data.map(row => (
            <div key={row.age} className="border border-border rounded-lg p-2.5 mx-0.5 bg-background">
              <p className="text-[10px] font-bold text-[var(--brand-primary)] mb-1.5 truncate">{row.age}</p>
              <div className="space-y-1">
                <div className="flex justify-between items-center gap-1">
                  <span className="text-[9px] text-muted-foreground uppercase tracking-widest">Opt-ins</span>
                  <span className="text-[10px] font-semibold" style={{ color: COLOR_OPTINS }}>{formatNumber(row.optins)}</span>
                </div>
                <div className="flex justify-between items-center gap-1">
                  <span className="text-[9px] text-muted-foreground uppercase tracking-widest">Spend</span>
                  <span className="text-[10px] font-semibold text-foreground">{formatCurrency(row.spend)}</span>
                </div>
                <div className="flex justify-between items-center gap-1">
                  <span className="text-[9px] text-muted-foreground uppercase tracking-widest">CPL</span>
                  <span className="text-[10px] font-semibold text-foreground">
                    {row.optins > 0 ? formatCurrency(row.spend / row.optins) : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ChartCard>
    </div>
  );
}
